import React, { useState } from 'react';
import { HeadlineLarge, HeadlineMedium } from '@shared/ui/atoms/fonts/Headline';
import styled from "styled-components";
import { BodyLarge, BodyRegular, BodySmall } from '@shared/ui/atoms/fonts/Body';
import { selectAccountInfo, selectIsAccountActive } from "@features/settings/store/payments.duck";
import { useDispatch, useSelector } from "react-redux";
import { ProgressBar } from '@shared/ui/atoms/ProgressBar';
import { Row } from '@shared/ui/atoms/StructuralLayout';
import { ContentCard } from '@shared/ui/atoms/ContentCard';
import Colours from '@shared/ui/atoms/Colours';
import { Trans, useTranslation } from "react-i18next";
import { AccountType } from '@shared/models/company.models';
import { PlanInfoCard } from "./PlanInfoCard";
import { PaymentFrequency } from '@features/auth/services/AccountService';
import moment from 'moment';
import { selectCurrentUser } from "@shared/store/auth.duck";
import { Permission } from "@shared/models/user.models";
import { DialogIdentifiers, openDialog } from "@shared/store/dialog.duck";
import { PrimaryButton } from '@shared/ui/atoms/buttons/PrimaryButton';
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';
import { OutlineButton } from "@shared/ui/atoms/buttons/OutlineButton";
import { tablet } from '@shared/ui/atoms/MediaQueries';
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import { getCustomerPortal } from '@shared/ui/organisms/stripe/stripeService';
import { failureNotification } from '@shared/store/notification.duck';
import { TrackJS } from 'trackjs';

// Max custom whereabouts per account type. Mirrors AccountTypeControlService.canCreateCustomWhereabouts
// on the backend (team-today-api) — keep in sync if those limits change.
const CUSTOM_WHEREABOUTS_LIMITS: Record<AccountType, number> = {
  [AccountType.STANDARD]: 1,
  [AccountType.PROFESSIONAL]: 6,
  [AccountType.PREMIUM]: 32,
  [AccountType.ENTERPRISE]: 32,
  [AccountType.FREE_TRIAL]: 32,
};

// Standard plans include 3 meeting rooms.
const STANDARD_MEETING_ROOMS_LIMIT = 3;

export default function PlanUsageContentCard() {
  const accountInfo = useSelector(selectAccountInfo);
  const isAccountActive = useSelector(selectIsAccountActive);
  const currentUser = useSelector(selectCurrentUser);
  const isAdmin = currentUser?.role === Permission.CompanyAdmin;
  const dispatch = useDispatch();
  const {t} = useTranslation();

  const openManageDialog = () => dispatch(openDialog(DialogIdentifiers.ManageSubscription));

  const [isLoading, setIsLoading] = useState(false);

  const openStripePortal = async () => {
    try {
      setIsLoading(true);
      window.location.href = (await getCustomerPortal()).url;
    } catch (e: any) {
      console.error(e);
      TrackJS?.track(e);
      dispatch(failureNotification('There has been an issue opening the Stripe payments portal'));
    } finally {
      setIsLoading(false);
    }
  };

  const userLimit = accountInfo?.userLimit ?? 0;
  const totalUsersUsed = accountInfo?.totalUsers ?? 0;
  const paidLicences = accountInfo?.paidUsers ?? 0;
  const customWhereaboutsLimit = CUSTOM_WHEREABOUTS_LIMITS[accountInfo?.accountType ?? AccountType.STANDARD];
  const meetingRoomsCount = accountInfo?.meetingRoomsCount ?? 0;
  // Standard plans include 3 meeting rooms; show that entitlement rather than the live count.
  const isStandard = accountInfo?.accountType === AccountType.STANDARD;
  const meetingRoomsNumber = isStandard ? STANDARD_MEETING_ROOMS_LIMIT : meetingRoomsCount;
  const usesStripe = !!accountInfo?.usesStripe;
  const costSummaryKey = accountInfo?.paymentFrequency === PaymentFrequency.Yearly
    ? 'payments.plan-cost-summary-yearly'
    : 'payments.plan-cost-summary-monthly';
  const formattedCost = accountInfo?.unitAmount != null
    ? new Intl.NumberFormat(undefined, { style: 'currency', currency: (accountInfo.currency || 'GBP').toUpperCase() }).format(accountInfo.billTotal / 100)
    : '';
  const renewalDate = accountInfo?.endDateString ? moment(accountInfo.endDateString).format('Do MMM YYYY') : '';
  const costSummaryParts: string[] = [];
  if (accountInfo?.unitAmount != null && accountInfo?.paymentFrequency) {
    costSummaryParts.push(t(costSummaryKey, { cost: formattedCost }));
  }
  if (renewalDate && accountInfo?.accountType !== AccountType.STANDARD) {
    costSummaryParts.push(t('payments.next-renewal', { date: renewalDate }));
  }
  const costSummary = costSummaryParts.join(' - ');

  const planLabel = (type?: AccountType) => {
    switch (type) {
      case AccountType.PREMIUM: return t('payments.premium');
      case AccountType.ENTERPRISE: return t('payments.enterprise');
      case AccountType.PROFESSIONAL: return t('payments.professional');
      case AccountType.FREE_TRIAL: return t('payments.free-trial');
      default: return t('payments.standard');
    }
  };

  const isPremium = accountInfo?.accountType === AccountType.PREMIUM || accountInfo?.accountType === AccountType.ENTERPRISE;
  const isProfessionalOrPremium = accountInfo?.accountType === AccountType.PROFESSIONAL || isPremium;

  const features: { label: string; note?: string; enabled: boolean }[] = [
    { label: t('payments.custom-whereabouts-count', { number: customWhereaboutsLimit, limit: customWhereaboutsLimit }), enabled: customWhereaboutsLimit > 0 },
    { label: t('payments.meeting-rooms-count', { number: meetingRoomsNumber }), enabled: meetingRoomsNumber > 0 },
    { label: t('payments.advanced-reporting'), enabled: isProfessionalOrPremium },
    { label: t('payments.priority-support'), enabled: isProfessionalOrPremium },
    { label: t('payments.interactive-floor-plans'), enabled: isPremium },
    { label: t('payments.car-parking'), enabled: isPremium },
  ];

  return (
    <>
      {isLoading && <LoadingSpinner fullScreen={true} />}
      <BodyLarge style={{marginBottom: 24}}>{t('settings.account-usage')}</BodyLarge>
      <ContentCard style={{marginBottom: 48}}>
        <HeadlineMedium style={{marginBottom: 12}}>{t('payments.current-plan')}</HeadlineMedium>
        <PillRow>
          <PlanPill>
            <BodyRegular weight={600} colour={Colours.blue}>{t('payments.plan-pill', { plan: planLabel(accountInfo?.accountType) })}</BodyRegular>
          </PlanPill>
          {accountInfo?.paymentFrequency &&
            <PlanPill>
              <BodyRegular weight={600} colour={Colours.blue}>
                {t(accountInfo.paymentFrequency === PaymentFrequency.Yearly ? 'payments.annual-plan' : 'payments.monthly-plan')}
              </BodyRegular>
            </PlanPill>}
        </PillRow>
        <UsageBlock>
          <Row>
            <CountWrapper>
              <BodyRegular weight={600}>{t('settings.users-registered')}</BodyRegular>
              <HeadlineLarge style={{marginBottom: 8}}>{totalUsersUsed}</HeadlineLarge>
            </CountWrapper>
            <CountWrapper>
              <BodyRegular weight={600} colour={Colours.blue}>{t('settings.spaces-left')}</BodyRegular>
              <HeadlineLarge style={{marginBottom: 8}}>{userLimit - totalUsersUsed}</HeadlineLarge>
            </CountWrapper>
          </Row>
          <ProgressBar complete={totalUsersUsed / userLimit * 100}/>
        </UsageBlock>
        <PlanInfoCard weight={600}>{t('payments.total-licences', { licences: paidLicences })}</PlanInfoCard>
        <FeatureList>
          {features.map((feature, index) => (
            <FeatureRow key={index} $enabled={feature.enabled}>
              <BodySmall>{feature.label}{feature.note ? <Note> ({feature.note})</Note> : null}</BodySmall>
            </FeatureRow>
          ))}
        </FeatureList>

        {usesStripe ? (
          <>
            <PaymentPlanRow>
              <BodyRegular weight={600}>{t('payments.your-payment-plan')}</BodyRegular>
              <img src="/assets/icons/powered-by-stripe-purple.svg" alt="Powered by Stripe" />
            </PaymentPlanRow>
            {costSummary &&
              <PlanInfoCard weight={600} style={{ marginTop: 16 }}>{costSummary}</PlanInfoCard>}
            {isAdmin && isAccountActive && <>
              <ButtonRow>
                <OutlineButton size={ButtonSize.Large}
                               click={openStripePortal}
                               text="Manage Payment Method"
                               style={{ maxWidth: 300 }} />

                <PrimaryButton size={ButtonSize.Large}
                               click={openManageDialog}
                               text="payments.manage-subscription"
                               style={{ maxWidth: 300 }} />
              </ButtonRow>
            </>}
          </>
        ) : (
          <BodyRegular weight={400} style={{ marginTop: 24, maxWidth: 500, justifySelf: 'center' }}>
            <Trans i18nKey={'settings.cancel-message'}
                   components={[<a style={{ color: Colours.blue }} href={'mailto:support@team-today.com'}>support@team-today.com</a>]} />
          </BodyRegular>
        )}
      </ContentCard>
    </>
  );
}

const UsageBlock = styled.div`
  margin-bottom: 48px;
`

const PillRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 32px;
`

const PlanPill = styled.div`
  display: inline-flex;
  align-items: center;
  border: 1px solid ${Colours.blue};
  border-radius: 64px;
  padding: 4px 18px;
`

const CountWrapper = styled.div`
  width: 114px;
  display: flex;
  flex-direction: column;
  flex: 1;
`

const FeatureList = styled.ul`
  margin: 16px 0 0;
  padding: 0;
  list-style: none;
`

const FeatureRow = styled.li<{ $enabled: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
  &::before {
    content: '';
    flex-shrink: 0;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: ${props => props.$enabled ? Colours.green : Colours.mildGrey};
  }
  p {
    margin: 0;
    color: ${props => props.$enabled ? Colours.black : Colours.mildGrey};
    text-decoration: ${props => props.$enabled ? 'none' : 'line-through'};
  }
`

const Note = styled.span`
  color: ${Colours.darkGrey};
`

const PaymentPlanRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 24px;
`

const ButtonRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  margin-top: 24px;
  @media (${tablet}) {
    justify-content: space-between;
    flex-direction: row;
  }
`
